import { BreaksTableWidget, BreaksTableWidgetProps } from '@/fragments/BreaksTableWidget/BreaksTableWidget'
import { WidgetElementMappingType } from '@/fragments/MosaicLayout/MosaicLayout'
import { OperationsGanttWidgetContextProvider } from '@/fragments/OperationsGanttChart/OperationsGanttChartContext'
import { PlanningGanttChart } from '@/fragments/OperationsGanttChart/PlanningGanttChart'
import { PlanningOperationsGanttWidgetToolbar } from '@/fragments/OperationsGanttChart/PlanningOperationsGanttWidgetToolbar'
import { RunsheetGanttChart } from '@/fragments/OperationsGanttChart/RunsheetGanttChart'
import { RunsheetOperationsGanttWidgetToolbar } from '@/fragments/OperationsGanttChart/RunsheetOperationsGanttWidgetToolbar'
import { OperationsMapWidget } from '@/fragments/OperationsMapWidget/OperationsMapWidget'
import { OperationsMapWidgetContextProvider } from '@/fragments/OperationsMapWidget/OperationsMapWidgetContext'
import { PlanningMapWidgetControl } from '@/fragments/OperationsMapWidget/PlanningMapWidgetControl'
import { PlanningOperationsMapWidgetToolbar } from '@/fragments/OperationsMapWidget/PlanningOperationsMapWidgetToolbar'
import { RunsheetMapWidgetControl } from '@/fragments/OperationsMapWidget/RunsheetMapWidgetControl'
import { RunsheetOperationsMapWidgetToolbar } from '@/fragments/OperationsMapWidget/RunsheetOperationsMapWidgetToolbar'
import { OperationsViolationsWidget } from '@/fragments/OperationsViolationsWidget/OperationsViolationsWidget'
import { PlanningInboxTable, PlanningInboxTableProps } from '@/fragments/PlanningInboxTable/PlanningInboxTable'
import { PlanningShiftTable, PlanningShiftTableProps } from '@/fragments/PlanningShiftTable/PlanningShiftTable'
import { PlanningShiftTableContextProvider } from '@/fragments/PlanningShiftTable/PlanningShiftTableContext'
import { PlanningShiftWindowToolbar } from '@/fragments/PlanningShiftTable/PlanningShiftWindowToolbar'
import { PlanningUnassignedTable, PlanningUnassignedTableProps } from '@/fragments/PlanningUnassignedTable/PlanningUnassignedTable'
import { RunsheetShiftTable, RunsheetShiftTableProps } from '@/fragments/RunsheetShiftTable/RunsheetShiftTable'
import { RunsheetShiftTableContextProvider } from '@/fragments/RunsheetShiftTable/RunsheetShiftTableContext'
import { RunsheetShiftWindowToolbar } from '@/fragments/RunsheetShiftTable/RunsheetShiftWindowToolbar'
import { RunsheetUnassignedTable, RunsheetUnassignedTableProps } from '@/fragments/RunsheetUnassignedTable/RunsheetUnassignedTable'
import { SummaryTable, SummaryTableProps } from '@/fragments/SummaryTable/SummaryTable'
import { SummaryTableContextProvider } from '@/fragments/SummaryTable/SummaryTableContext'
import { SummaryTableToolbarActions } from '@/fragments/SummaryTable/SummaryTableToolbarActions'
import { WidgetTypeWithSuffix } from '@/shared/components/Mosaic'
import { Typography } from '@mui/material'

export const operationWidgetElementMapping: WidgetElementMappingType = {
  'Map': {
    CustomWindowContext: OperationsMapWidgetContextProvider, // Shared map context between widget element and toolbar
    widgetElement: (props: { id: string, widgetId: WidgetTypeWithSuffix }) => (
      <OperationsMapWidget {...props}>
        <PlanningMapWidgetControl />
      </OperationsMapWidget>
    ),
    isAdditionalToolbarEnabled: true,
    additionalToolbarElement: <PlanningOperationsMapWidgetToolbar />,
  },
  'Violations': {
    widgetElement: (props: { id: string, widgetId: WidgetTypeWithSuffix }) => (
      <OperationsViolationsWidget {...props} />
    ),
  },
  'Geocode': {
    widgetElement: () => (<Typography variant="h2">Geocode</Typography>),
  },
  'GanttChart': {
    CustomWindowContext: OperationsGanttWidgetContextProvider, // Shared gantt cahrt context between widget element and toolbar
    widgetElement: (props: { id: string, widgetId: WidgetTypeWithSuffix }) => (
      <PlanningGanttChart {...props} />
    ),
    isAdditionalToolbarEnabled: true,
    additionalToolbarElement: <PlanningOperationsGanttWidgetToolbar />,
  },
  'A2BRoute': {
    widgetElement: () => (<Typography variant="h2">A2BRoute</Typography>),
  },
  'Solver': {
    widgetElement: () => (<Typography variant="h2">Sovler</Typography>),
  },
  'ScenarioSummaryTable': {
    CustomWindowContext: SummaryTableContextProvider,
    widgetElement: (props: Omit<SummaryTableProps, 'layoutType'>) => (<SummaryTable layoutType="planning" {...props} />),
    toolbarActions: <SummaryTableToolbarActions />,
  },
  'RunsheetSummaryTable': {
    CustomWindowContext: SummaryTableContextProvider,
    widgetElement: (props: Omit<SummaryTableProps, 'layoutType'>) => (<SummaryTable layoutType="runsheet" {...props} />),
    toolbarActions: <SummaryTableToolbarActions />,
  },
  'PlanningShiftTable': {
    CustomWindowContext: PlanningShiftTableContextProvider,
    widgetElement: (props: PlanningShiftTableProps) => (<PlanningShiftTable {...props} />),
    isAdditionalToolbarEnabled: true,
    additionalToolbarElement: <PlanningShiftWindowToolbar />,
  },
  'RunsheetShiftTable': {
    CustomWindowContext: RunsheetShiftTableContextProvider,
    widgetElement: (props: RunsheetShiftTableProps) => (<RunsheetShiftTable {...props} />),
    isAdditionalToolbarEnabled: true,
    additionalToolbarElement: <RunsheetShiftWindowToolbar />,
  },
  'RunsheetBreaks': {
    widgetElement: (props: BreaksTableWidgetProps) => (
      <BreaksTableWidget {...props} />
    ),
  },
  'RunsheetUnassignedOrders': {
    widgetElement: (props: RunsheetUnassignedTableProps) => (<RunsheetUnassignedTable {...props} />),
  },
  'ScenarioUnassignedOrders': {
    widgetElement: (props: PlanningUnassignedTableProps) => (<PlanningUnassignedTable {...props} />),
  },
  'Inbox': {
    widgetElement: (props: PlanningInboxTableProps) => (<PlanningInboxTable {...props} />),
  },
}

export const getOperationWidgetElementMapping = (layoutType: 'runsheet' | 'planning') => {
  const widgetElementMapping = operationWidgetElementMapping

  // Update the map widget element based on the layout type
  if (layoutType === 'runsheet') {
    widgetElementMapping.Map = {
      CustomWindowContext: OperationsMapWidgetContextProvider,
      widgetElement: (props: { id: string, widgetId: WidgetTypeWithSuffix }) => (
        <OperationsMapWidget {...props}>
          <RunsheetMapWidgetControl />
        </OperationsMapWidget>
      ),
      isAdditionalToolbarEnabled: true,
      additionalToolbarElement: <RunsheetOperationsMapWidgetToolbar />,
    }

    widgetElementMapping.GanttChart = {
      CustomWindowContext: OperationsGanttWidgetContextProvider, // Shared gantt cahrt context between widget element and toolbar
      widgetElement: (props: { id: string, widgetId: WidgetTypeWithSuffix }) => (
        <RunsheetGanttChart {...props} />
      ),
      isAdditionalToolbarEnabled: true,
      additionalToolbarElement: <RunsheetOperationsGanttWidgetToolbar />,
    }
  } else {
    widgetElementMapping.Map = {
      CustomWindowContext: OperationsMapWidgetContextProvider,
      widgetElement: (props: { id: string, widgetId: WidgetTypeWithSuffix }) => (
        <OperationsMapWidget {...props}>
          <PlanningMapWidgetControl />
        </OperationsMapWidget>
      ),
      isAdditionalToolbarEnabled: true,
      additionalToolbarElement: <PlanningOperationsMapWidgetToolbar />,
    }

    widgetElementMapping.GanttChart = {
      CustomWindowContext: OperationsGanttWidgetContextProvider, // Shared gantt cahrt context between widget element and toolbar
      widgetElement: (props: { id: string, widgetId: WidgetTypeWithSuffix }) => (
        <PlanningGanttChart {...props} />
      ),
      isAdditionalToolbarEnabled: true,
      additionalToolbarElement: <PlanningOperationsGanttWidgetToolbar />,
    }
  }

  return widgetElementMapping
}